import { useCallback, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable, sumDisplayed } from "@/shared/utils/gridToPdfTable";
import { usePartialRollsets } from "../hooks/usePartialRollsets";
import type { PartialRollsetRow } from "../models/PartialRollsets";
import { PartialRollsetsSummary } from "./PartialRollsetsSummary";
import { PartialRollsetsToolbar } from "./PartialRollsetsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const LEFT_CELL = { textAlign: "left" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

// The search matches what the user sees, not the raw float behind it.
const twoDecimals = {
	valueFormatter: (p: { value: unknown }) => toFixedOrEmpty(p.value, 2),
	getQuickFilterText: (p: { value: unknown }) => toFixedOrEmpty(p.value, 2),
};

const columnDefs: ColDef<PartialRollsetRow>[] = [
	{
		field: "StandNumber",
		headerName: "Stand",
		width: 100,
		sort: "asc",
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "BarrelDescription",
		headerName: "Description",
		flex: 1,
		minWidth: 200,
		sortable: true,
		cellStyle: LEFT_CELL,
		tooltipField: "BarrelDescription",
	},
	{
		field: "RollsetName",
		headerName: "Roll Set",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollPosition",
		headerName: "Roll Position",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Diameter",
		headerName: "Roll Diameter",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		...twoDecimals,
	},
	{
		field: "CurrentPercent",
		headerName: "Current Percent",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		...twoDecimals,
	},
	{
		field: "CurrentValue",
		headerName: "Current Value",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		...twoDecimals,
	},
	{
		field: "RollName",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

const URL_SCHEMA = { search: urlParam.string() };

export const PartialRollsetsReport = () => {
	const [{ search }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);
	// The box filters at once; the link follows 300 ms later.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<PartialRollsetRow> | null>(null);

	const dataQuery = usePartialRollsets();

	const rows = dataQuery.data ?? [];
	const [shown, setShown] = useState<number | null>(null);
	const [totalValue, setTotalValue] = useState(0);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Partial_Rollsets.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (
					colId === "Diameter" ||
					colId === "CurrentPercent" ||
					colId === "CurrentValue"
				) {
					return toFixedOrEmpty(params.value, 2);
				}
				return params.value ?? "";
			},
		});
	}, []);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportToPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			sums: [{ colId: "CurrentValue", label: "Total Value" }],
			aligns: {
				BarrelDescription: "left",
				Diameter: "right",
				CurrentPercent: "right",
				CurrentValue: "right",
			},
		});
		exportPdf(`Partial Rollsets ${moment().format("MM-DD-YYYY")}.pdf`, () =>
			getReportPdf({
				title: "Partial Rollsets",
				filters: searchText ? [{ label: "Search", value: searchText }] : [],
				tables: [table],
			}),
		);
	};

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Partial Rollsets
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<PartialRollsetsToolbar
					search={searchText}
					onSearchChange={setSearchText}
					onReset={() => resetValues()}
					canReset={!isDefault()}
					onExportExcel={handleExportToExcel}
					onExportPdf={handleExportToPdf}
					canExportPdf={!dataQuery.isLoading && (shown ?? rows.length) > 0}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			<PartialRollsetsSummary
				total={rows.length}
				shown={shown ?? rows.length}
				money={{ label: "Total Value", value: totalValue }}
			/>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<PartialRollsetRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						quickFilterText={searchText}
						onModelUpdated={(e) => {
							setShown(e.api.getDisplayedRowCount());
							setTotalValue(sumDisplayed(e.api, "CurrentValue"));
						}}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
